'use client';

import type { OptionsObject, SnackbarKey } from 'notistack';
import { enqueueSnackbar } from 'notistack';

import { FadeSlide } from '@libs/common-next';

type SnackbarTone = 'default' | 'success' | 'error' | 'info';

interface SnackbarOptions extends OptionsObject<'twoLine' | 'default'> {
    width?: string;
    tone?: SnackbarTone;
    secondaryText?: string;
    actionText?: string;
    onClick?: () => void;
}

type SnackbarProps = {
    primaryText: string;
    secondaryText?: string;
    actionText?: string;
    onClick?: () => void;
    autoDismiss?: boolean;
    preventDuplicate?: boolean;
    width?: string;
    tone?: SnackbarTone;
    key?: SnackbarKey;
    /**
     * By default notistack pauses autoHideDuration on window blur and only resumes it on window
     * focus. Pass true when the caller may show a same-page iframe/overlay (e.g. a Medallia
     * survey) while the snackbar is up — focusing that overlay blurs the top window, and closing
     * it doesn't reliably re-fire window focus, so the snackbar can otherwise get stuck forever.
     */
    disableWindowBlurListener?: boolean;
};

type PredefinedTypes = 'SERVER_ERROR';
export const SERVER_ERROR_PRIMARY_TEXT = 'Something went wrong, try again.';

const predefinedTypesData: { [K in PredefinedTypes]: SnackbarProps } = {
    SERVER_ERROR: {
        primaryText: SERVER_ERROR_PRIMARY_TEXT,
        actionText: 'Refresh',
        onClick: () => window.location.reload(),
        preventDuplicate: true,
    },
};

export const SnackbarUtils = {
    show({
        primaryText,
        secondaryText,
        actionText,
        onClick,
        autoDismiss = true,
        width,
        tone = 'default',
        ...extraOptions
    }: SnackbarProps) {
        const commonOptions: Partial<OptionsObject> = {
            anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
            TransitionComponent: FadeSlide,
            autoHideDuration: autoDismiss ? 5000 : null,
        };

        const snackbarOptions: SnackbarOptions = {
            ...commonOptions,
            ...extraOptions,
            width,
            tone,
            variant: 'twoLine',
            secondaryText,
            actionText,
            onClick,
        };
        enqueueSnackbar(primaryText, snackbarOptions);
    },

    showPredefined(type: PredefinedTypes) {
        this.show(predefinedTypesData[type]);
    },
};
